import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, CircularProgress, Divider, Paper, Typography } from "@mui/material";
import {
  CurrentValueBox,
  MAX_REMARK_LENGTH,
  RemarkField,
  StyledTextField,
  ViewHeader,
} from "./EditDialogShared";
import { PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { useToast } from "../../Hooks/useToast";
import { useVehicleInfo } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { useOrgLabels } from "../../Hooks/usePagesContent";
import { getRbacFleets } from "../../Api/RbacApi";
import { getFleets } from "../../Api/FleetOverviewApi";
import { useCanUseGlobalAdvancedFilter } from "../../Helper/Permissions/GlobalAdvancedFilter";
import { updateDigitalTwin } from "../../Api/DigitalTwinListApi";
import CODE from "../../Static/Constants/StatusCodes";
import { translateErrorMessage } from "../../Helper/Common/FieldTranslator";
import { customerAccountOptions } from "../../Helper/Common/customerAccountOptions";

// Matches ChangeOriginLocationView: MUI positions the end adornment absolutely,
// which sits it off-centre against this input height.
const AUTOCOMPLETE_SX = {
  "& .MuiAutocomplete-endAdornment": {
    top: "50% !important",
    transform: "translateY(-50%) !important",
  },
};

export default function ChangeCustomerAccountView({ onBack, onSuccess }) {
  const { openNotification } = useToast();
  const { fleetLabel, fleetPluralLabel, fleetCategoryLabel } = useOrgLabels();
  // The same branch every other caller makes. getRbacFleets returns an empty list
  // for a user with no fleet assignments, and does so with a success code, so
  // calling it unconditionally (or on v1, where hasPermission is always true)
  // leaves a legacy org with a silently empty dropdown.
  const canUseRbacFleets = useCanUseGlobalAdvancedFilter();
  const {
    configBOPID,
    fleetName: currentFleetName,
    fleetCategory: currentFleetCategory,
  } = useVehicleInfo();

  const [fleets, setFleets] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(false);
  const [selectedFleet, setSelectedFleet] = useState(null);
  const [remark, setRemark] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // The same source the global filter uses, so a user is never offered a customer
  // they cannot otherwise see. It already carries the category, which is what lets
  // the business segment fill in without a second call.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = canUseRbacFleets ? await getRbacFleets() : await getFleets();
        if (cancelled) return;
        if (res?.responseStatus?.code !== CODE.SUCCESS) throw new Error(res?.responseStatus?.msg);
        setFleets(
          (res?.response?.data || []).map((fleet) => ({
            id: fleet.fleetID,
            name: fleet.fleetName,
            category: fleet.category,
          }))
        );
      } catch (error) {
        if (!cancelled) setDataError(true);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUseRbacFleets]);

  // Everything except the customer this vehicle already belongs to: selecting it
  // again would be a no-op the API still writes.
  const options = useMemo(
    () => customerAccountOptions(fleets, currentFleetName),
    [fleets, currentFleetName]
  );

  const canSubmit = Boolean(configBOPID) && options.length > 0;

  const handleSubmit = async () => {
    if (!configBOPID) {
      setErrors({ fleet: `This vehicle has no record to update its ${fleetLabel.toLowerCase()} against` });
      return;
    }
    const errs = {};
    if (!selectedFleet) errs.fleet = `Please select a ${fleetLabel.toLowerCase()}`;
    // Required, as on every other edit: this is the change most likely to be
    // questioned later, and the remark is the only place the reason is recorded.
    if (!remark.trim()) errs.remark = true;
    if (remark.length > MAX_REMARK_LENGTH) errs.remarkCount = true;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setIsLoading(true);
    try {
      const response = await updateDigitalTwin({
        id: configBOPID,
        fleetName: selectedFleet.name,
        remarks: remark,
      });
      if (response?.responseStatus?.code !== CODE.SUCCESS) {
        throw new Error(response?.responseStatus?.msg);
      }
      await onSuccess(`${fleetLabel} Updated!`);
    } catch (error) {
      const msg =
        translateErrorMessage(error?.message) || `Could not update ${fleetLabel.toLowerCase()}`;
      setErrors({ fleet: msg });
      openNotification({ text: msg, icon: "toast-error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <ViewHeader title={`Change ${fleetLabel}`} onBack={onBack} />

      {/* Paired: the segment is a property of the customer, so reading them on one
          line says so, and keeps the dialog from scrolling on a short screen. */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <CurrentValueBox
          label={`Current ${fleetLabel}`}
          value={currentFleetName}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <CurrentValueBox
          label={`Current ${fleetCategoryLabel}`}
          value={currentFleetCategory}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>

      <Divider sx={{ borderColor: "#EFEFEF", mb: 2.5 }} />

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ mb: 1.5, flex: 1, minWidth: 0 }}>
          {/* mb matches CurrentValueBox's label gap, so the two fields align. */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Typography variant="fw400s14c000000" sx={{ lineHeight: "24px", color: "#494949" }}>
              New {fleetLabel}
            </Typography>
            <Typography
              variant="fw400s14c000000"
              sx={{ fontWeight: 700, lineHeight: "24px", color: "#B50500" }}
            >
              &nbsp;*
            </Typography>
          </Box>

          {dataLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Autocomplete
              fullWidth
              disabled={dataError}
              options={options}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={selectedFleet}
              onChange={(_, newValue) => setSelectedFleet(newValue)}
              sx={AUTOCOMPLETE_SX}
              data-testid="change-customer-account-select"
              PaperComponent={(props) => (
                <Paper
                  {...props}
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    "& .MuiAutocomplete-option": { fontSize: "14px" },
                  }}
                />
              )}
              slotProps={{ listbox: { sx: { fontSize: "14px" } } }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  placeholder={
                    dataError
                      ? `Could not load ${fleetPluralLabel.toLowerCase()}`
                      : `Search or select ${fleetLabel.toLowerCase()}`
                  }
                  error={!!errors.fleet}
                />
              )}
            />
          )}

          {!dataLoading && !dataError && options.length === 0 && (
            <Typography sx={{ fontSize: "12px", color: "#686868", mt: 0.5 }}>
              No other {fleetPluralLabel.toLowerCase()} are available to move this vehicle to.
            </Typography>
          )}

          {errors.fleet && (
            <Typography sx={{ fontSize: "10px", color: "#930500", mt: 0.5 }}>
              {errors.fleet}
            </Typography>
          )}
        </Box>

        {/* Derived from the chosen customer rather than asked for: the segment is a
            property of the customer, so offering it as a second choice would let the
            two disagree. Always rendered, so choosing a customer fills a field that
            is already there rather than growing the form under the pointer. */}
        <CurrentValueBox
          label={`New ${fleetCategoryLabel}`}
          value={selectedFleet ? selectedFleet.category : "—"}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>

      <RemarkField
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        error={errors.remark}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <PrimaryFormLoadingActionButton
          disabled={isLoading || dataLoading || dataError || !canSubmit}
          onClick={handleSubmit}
          loading={isLoading}
          sx={{ borderRadius: "4px", px: 3 }}
          data-testid="change-customer-account-confirm"
        >
          Confirm Change
        </PrimaryFormLoadingActionButton>
      </Box>
    </Box>
  );
}
